// Set to 'classic' to restore the previous calendar, header, panels, and modals.
// Keep both palettes here so this visual experiment is reversible in one place.
export const calendarTheme = 'casino';
export const isCasinoTheme = calendarTheme === 'casino';

// Original champagne, amber, and warm wood theme, preserved unchanged.
const classicPalette = {
  canvas: '#f8efdf',
  canvasEnd: '#fbf8f1',
  canvasGlow: '#efd6a4',
  surface: '#fffdfa',
  daySurface: '#ffffff',
  surfaceMuted: '#f7f0e5',
  monthHeader: '#f3dfb7',
  ink: '#3e3027',
  muted: '#766351',
  border: '#d8c8b2',
  dayBorder: '#c6b9a6',
  gridBorder: '#e1d6c7',
  heading: '#533b2b',
  heroMid: '#6b4328',
  heroEnd: '#82552f',
  accent: '#94521e',
  accentInk: '#94521e',
  accentHover: '#764019',
  accentTint: '#fbefdc',
  accentBorder: '#bb9166',
  // Role colors stay separate from the warm surfaces and shared controls.
  adminAccent: '#435bc4',
  adminAccentHover: '#34489f',
  adminAccentTint: '#edf0fc',
  adminAccentBorder: '#a6b1e2',
  clientAccent: '#b33d32',
  clientAccentHover: '#922f27',
  clientAccentTint: '#fcebe7',
  clientAccentBorder: '#d9958c',
  // Original calendar colors, independent of the action buttons.
  adminCalendarAccent: '#3142ff',
  adminCalendarSelection: '#6060ff',
  adminCalendarHover: '#5050e6',
  adminCalendarTint: '#eeeeff',
  adminCalendarBorder: '#a6a6ff',
  adminCalendarAvailable: '#dcdcff',
  adminCalendarAvailableBorder: '#9393df',
  adminCalendarAvailableHover: '#ccccfa',
  clientCalendarAccent: '#ce1d19',
  clientCalendarSelection: '#ce1d19',
  clientCalendarHover: '#b71916',
  clientCalendarTint: '#ffe4e4',
  clientCalendarBorder: '#df8b87',
  clientCalendarAvailable: '#fcdcd8',
  clientCalendarAvailableInk: '#a62e27',
  clientCalendarAvailableBorder: '#ca8178',
  clientCalendarAvailableHover: '#f6c7c1',
  gold: '#dca33b',
  booked: '#f6dc91',
  bookedInk: '#674b17',
  bookedBorder: '#bd953d',
  unavailable: '#ff0000',
  disabled: '#978a7c',
  onDark: '#ffffff',
};

// Idea 1: ivory surfaces, espresso headings, mahogany admin accents, and clear client reds.
const casinoPalette = {
  ...classicPalette,
  canvas: '#f4efe6',
  canvasEnd: '#f7f3ec',
  canvasGlow: '#eadcbc',
  surface: '#fffdf9',
  daySurface: '#fffdf9',
  surfaceMuted: '#f3ece1',
  modalSurround: '#ded0bd',
  monthHeader: '#30231d',
  ink: '#30241e',
  muted: '#796b60',
  border: '#dacbb7',
  dayBorder: '#cfc2b1',
  gridBorder: '#e4d9ca',
  heading: '#30231d',
  heroMid: '#49382d',
  heroEnd: '#745337',
  accent: '#855b30',
  accentInk: '#855b30',
  accentHover: '#6c4724',
  accentTint: '#f5eadd',
  accentBorder: '#b89a72',
  // Espresso inspector with warm brown rows and a darker golden brown menu.
  panel: '#423129',
  panelDeep: '#30231d',
  panelRow: '#68513e',
  panelHover: '#765c46',
  menuBackground: '#583f2d',
  menuHighlight: '#fff0d7',
  // Reddish brown for administrators, brighter red for clients.
  adminAccent: '#684137',
  adminAccentHover: '#513027',
  adminAccentTint: '#f4e8e2',
  adminAccentBorder: '#b58b7c',
  adminCalendarAccent: '#684137',
  adminCalendarSelection: '#684137',
  adminCalendarHover: '#513027',
  adminCalendarTint: '#f4e8e2',
  adminCalendarBorder: '#b58b7c',
  adminCalendarAvailable: '#ebd8ce',
  adminCalendarAvailableBorder: '#ad7d6d',
  adminCalendarAvailableHover: '#e2c8bb',
  clientAccent: '#cc2922',
  clientAccentHover: '#aa201b',
  clientAccentTint: '#ffefec',
  clientAccentBorder: '#de9087',
  clientCalendarAccent: '#cc2922',
  clientCalendarSelection: '#cc2922',
  clientCalendarHover: '#aa201b',
  clientCalendarTint: '#ffefec',
  clientCalendarBorder: '#de9087',
  clientCalendarAvailable: '#fce0db',
  clientCalendarAvailableInk: '#bc2b22',
  clientCalendarAvailableBorder: '#d77d73',
  clientCalendarAvailableHover: '#f8cdc6',
  gold: '#be9850',
  unavailable: '#e1261c',
};

// CSS variables let every consumer (including portals) switch together without
// changing booking state. Fallbacks preserve the original light appearance on
// pages that have not adopted the shared theme yet.
export const calendarPalette = isCasinoTheme
  ? Object.fromEntries(Object.entries(casinoPalette).map(([name, value]) => [
      name, `var(--casino-${name}, ${value})`,
    ]))
  : classicPalette;

// A subtle lift for client booked dates, shared with their legend.
// Scoped overrides leave the light palette and admin calendar unchanged.
export const clientCalendarDarkStates = `
  html[data-casino-theme='dark'] & {
    --casino-booked: #715728;
    --casino-bookedInk: #ffe8b2;
    --casino-bookedBorder: #c29b54;
  }
`;

// Keep the original light-mode blues; dark mode overrides these shared tokens
// for date cells, the availability legend, and the mimic selection controls.
const mimicLightPalette = {
  accent: classicPalette.adminCalendarAccent,
  selection: classicPalette.adminCalendarSelection,
  hover: classicPalette.adminCalendarHover,
  tint: classicPalette.adminCalendarTint,
  border: classicPalette.adminCalendarBorder,
  available: classicPalette.adminCalendarAvailable,
  availableBorder: classicPalette.adminCalendarAvailableBorder,
  availableHover: classicPalette.adminCalendarAvailableHover,
};

export const mimicCalendarPalette = isCasinoTheme
  ? Object.fromEntries(Object.entries(mimicLightPalette).map(([name, value]) => [
      name, `var(--casino-mimic-${name}, ${value})`,
    ]))
  : mimicLightPalette;
